import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
    GasPump, Wrench, X, Plus, ChartBar, ArrowsCounterClockwise, Trash, Info, CheckCircle, Path, Clock, PencilSimple,
} from "@phosphor-icons/react";

const fmtZAR = (n) =>
    "R " + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtL = (n) =>
    (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) + " L";
const fmtKm = (n) =>
    (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) + " km";
import { fmtDuration } from "../lib/duration";
const fmtMin = (n) => fmtDuration(n);

const CATEGORY_LABEL = {
    fel: "FEL", adt: "ADT", excavator: "Excavator", forklift: "Forklift", passenger_vehicle: "Passenger",
};

/** ============================================================
 *  Fuel + Per-Machine Breakdown Cost monthly report
 *  Fleet Manager logs fuel + breakdowns; everyone with finance/manager
 *  access can view. Click any row → detail modal with full history + YTD.
 *  ============================================================ */
export default function FleetFuelReport() {
    const { user } = useAuth();
    const canLog = ["admin", "fleet_manager"].includes(user?.role);
    const monthDefault = new Date().toISOString().slice(0, 7);
    const [period, setPeriod] = useState(monthDefault);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // {type:'fuel'|'bk'|'detail', machine?}

    const load = async (lbl) => {
        setLoading(true);
        try {
            const r = await api.get("/fleet/machines-monthly", { params: { period_label: lbl } });
            setReport(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(period); }, [period]);

    const totals = report?.totals || {};

    return (
        <div className="space-y-4" data-testid="fleet-fuel-report-tab">
            <div className="g-card overflow-hidden">
                <div className="g-card-header-dark flex items-center gap-2">
                    <GasPump size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide flex-1">
                        Fuel &amp; cost report · per machine
                    </div>
                    <input
                        type="month"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="g-input !py-1 !text-[11px] bg-white text-slate-900"
                        data-testid="fuel-period-picker"
                    />
                    <button onClick={() => load(period)} className="ml-2 text-white hover:opacity-80" title="Refresh">
                        <ArrowsCounterClockwise size={14} />
                    </button>
                </div>

                {report && (
                    <div className="p-4 space-y-4">
                        {/* Snapshot indicator */}
                        {report.from_snapshot && (
                            <div className="flex items-center gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                                <CheckCircle size={12} weight="fill" style={{ color: "var(--grindrod-green)" }} />
                                <span>This month is frozen — viewing the auto-snapshot taken on{" "}
                                    {new Date(report.snapshot_at).toLocaleDateString()}.</span>
                            </div>
                        )}

                        {/* Totals strip */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3" data-testid="fuel-totals">
                            <Totals label="Machines tracked" value={totals.machines_count || 0} Icon={Info} />
                            <Totals label="Fuel litres" value={fmtL(totals.fuel_litres)} Icon={GasPump} accent="green" />
                            <Totals label="Fuel cost" value={fmtZAR(totals.fuel_cost_zar)} Icon={GasPump} />
                            <Totals label="Breakdown cost" value={fmtZAR(totals.breakdown_cost_zar)} Icon={Wrench} accent="red" />
                        </div>

                        {/* Action buttons (fleet manager only) */}
                        {canLog && (
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setModal({ type: "fuel" })}
                                    className="g-btn-primary text-xs"
                                    data-testid="log-fuel-btn"
                                >
                                    <Plus size={12} weight="bold" /> Log fuel
                                </button>
                                <button
                                    onClick={() => setModal({ type: "bk" })}
                                    className="g-btn-outline text-xs"
                                    data-testid="log-machine-breakdown-btn"
                                >
                                    <Wrench size={12} weight="bold" /> Log breakdown cost
                                </button>
                            </div>
                        )}

                        {/* Per-machine table */}
                        <div className="g-card overflow-hidden">
                            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-2">
                                <ChartBar size={12} weight="fill" /> All machines · {period}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs tabular" data-testid="fuel-report-machines">
                                    <thead className="bg-white text-slate-500 uppercase text-[10px]">
                                        <tr>
                                            <th className="text-left p-2">Machine</th>
                                            <th className="text-left p-2">Category</th>
                                            <th className="text-left p-2">Owner</th>
                                            <th className="text-left p-2">Type</th>
                                            <th className="text-right p-2">Fuel litres</th>
                                            <th className="text-right p-2">Fuel cost</th>
                                            <th className="text-right p-2">Breakdown cost</th>
                                            <th className="text-right p-2">Total cost</th>
                                            <th className="text-right p-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(report.machines || []).map((m) => (
                                            <tr
                                                key={m.machine_id}
                                                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                                onClick={() => setModal({ type: "detail", machine: m })}
                                                data-testid={`fuel-row-${m.machine_number}`}
                                            >
                                                <td className="p-2 font-bold text-[var(--grindrod-navy)]">{m.machine_number}</td>
                                                <td className="p-2 capitalize">{CATEGORY_LABEL[m.category] || "—"}</td>
                                                <td className="p-2">
                                                    <span className={`g-pill normal-case tracking-normal ${m.owner_division === "GTRB" ? "g-pill-navy" : "g-pill-amber"}`}>
                                                        {m.owner_division || "—"}
                                                    </span>
                                                </td>
                                                <td className="p-2">
                                                    {m.is_hired
                                                        ? <span className="g-pill normal-case tracking-normal" style={{ color: "#B36B00", background: "#FFF8E1" }}>Hired</span>
                                                        : <span className="g-pill normal-case tracking-normal" style={{ color: "var(--grindrod-navy)", background: "#E2E8F0" }}>Owned</span>}
                                                </td>
                                                <td className="p-2 text-right font-semibold">{fmtL(m.fuel_litres)}</td>
                                                <td className="p-2 text-right">{fmtZAR(m.fuel_cost_zar)}</td>
                                                <td className={`p-2 text-right ${m.breakdown_cost_zar > 0 ? "text-[var(--grindrod-red)] font-bold" : "text-slate-400"}`}>
                                                    {fmtZAR(m.breakdown_cost_zar)}
                                                </td>
                                                <td className="p-2 text-right font-bold tabular text-[var(--grindrod-navy)]">{fmtZAR(m.total_cost_zar)}</td>
                                                <td className="p-2 text-right">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setModal({ type: "detail", machine: m }); }}
                                                        className="g-btn-ghost-sm text-[10px]"
                                                        data-testid={`view-machine-${m.machine_number}`}
                                                        aria-label={`View ${m.machine_number} details`}
                                                    >
                                                        <Info size={11} /> View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {(report.machines || []).length === 0 && (
                                            <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">
                                                No machines configured yet.
                                            </td></tr>
                                        )}
                                    </tbody>
                                    {(report.machines || []).length > 0 && (
                                        <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                                            <tr>
                                                <td colSpan={4} className="p-2 font-bold uppercase text-[10px] tracking-wider text-slate-600">Totals</td>
                                                <td className="p-2 text-right font-bold">{fmtL(totals.fuel_litres)}</td>
                                                <td className="p-2 text-right font-bold">{fmtZAR(totals.fuel_cost_zar)}</td>
                                                <td className="p-2 text-right font-bold text-[var(--grindrod-red)]">{fmtZAR(totals.breakdown_cost_zar)}</td>
                                                <td className="p-2 text-right font-bold text-[var(--grindrod-navy)]">{fmtZAR(totals.total_cost_zar)}</td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </div>
                    </div>
                )}
                {!report && loading && (
                    <div className="p-12 text-center text-slate-400 text-sm">Loading fuel report…</div>
                )}
            </div>

            {modal?.type === "fuel" && (
                <FuelEntryModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(period); }} />
            )}
            {modal?.type === "bk" && (
                <MachineBreakdownModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(period); }} />
            )}
            {modal?.type === "detail" && (
                <MachineDetailModal
                    machineRow={modal.machine}
                    period={period}
                    canLog={canLog}
                    onClose={() => setModal(null)}
                    onChanged={() => load(period)}
                />
            )}
        </div>
    );
}

function Totals({ label, value, Icon, accent }) {
    const color = accent === "red" ? "var(--grindrod-red)" : accent === "green" ? "var(--grindrod-green)" : "var(--grindrod-navy)";
    return (
        <div className="g-card p-3">
            <div className="flex items-center gap-2 mb-1">
                <Icon size={14} weight="fill" style={{ color }} />
                <div className="uppercase-label text-[9.5px]">{label}</div>
            </div>
            <div className="font-display font-black text-xl tabular" style={{ color }}>{value}</div>
        </div>
    );
}

/* ---------- Fuel entry modal ---------- */
function FuelEntryModal({ initialMachineId, onClose, onSaved }) {
    const [machines, setMachines] = useState([]);
    const [form, setForm] = useState({
        machine_id: initialMachineId || "",
        entry_date: new Date().toISOString().slice(0, 10),
        litres: "",
        cost_zar: "",
        hours_reading: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/fel/machines").then((r) => setMachines(r.data || []))
            .catch((e) => toast.error(formatApiError(e)));
    }, []);

    const save = async () => {
        if (!form.machine_id) return toast.error("Pick a machine");
        if (!form.litres || Number(form.litres) <= 0) return toast.error("Litres must be > 0");
        setSaving(true);
        try {
            await api.post("/fleet/fuel", {
                machine_id: form.machine_id,
                entry_date: form.entry_date,
                litres: Number(form.litres),
                cost_zar: Number(form.cost_zar || 0),
                hours_reading: form.hours_reading ? Number(form.hours_reading) : null,
                notes: form.notes || null,
            });
            toast.success("Fuel entry saved");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        setSaving(false);
    };

    return (
        <ModalShell title="Log fuel consumption" onClose={onClose} testid="fuel-entry-modal">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Machine">
                    <select
                        className="g-input"
                        value={form.machine_id}
                        onChange={(e) => setForm({ ...form, machine_id: e.target.value })}
                        data-testid="fuel-machine-select"
                    >
                        <option value="">Select machine…</option>
                        {machines.map((m) => (
                            <option key={m.id} value={m.id}>
                                {m.machine_number} · {CATEGORY_LABEL[m.category] || ""} {m.is_hired ? "(hired)" : ""}
                            </option>
                        ))}
                    </select>
                </Field>
                <Field label="Date">
                    <input
                        type="date"
                        className="g-input"
                        value={form.entry_date}
                        onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                        data-testid="fuel-date"
                    />
                </Field>
                <Field label="Litres">
                    <input
                        type="number"
                        inputMode="decimal"
                        step="any"
                        min="0"
                        className="g-input tabular"
                        value={form.litres}
                        onChange={(e) => setForm({ ...form, litres: e.target.value })}
                        data-testid="fuel-litres"
                    />
                </Field>
                <Field label="Cost (R)">
                    <input
                        type="number"
                        inputMode="decimal"
                        step="any"
                        min="0"
                        className="g-input tabular"
                        value={form.cost_zar}
                        onChange={(e) => setForm({ ...form, cost_zar: e.target.value })}
                        data-testid="fuel-cost"
                    />
                </Field>
                <Field label="Hour / odometer reading (optional)">
                    <input
                        type="number"
                        inputMode="decimal"
                        step="any"
                        min="0"
                        className="g-input tabular"
                        value={form.hours_reading}
                        onChange={(e) => setForm({ ...form, hours_reading: e.target.value })}
                        data-testid="fuel-hours"
                    />
                </Field>
                <Field label="Notes (optional)">
                    <input
                        type="text"
                        className="g-input"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        maxLength={300}
                        data-testid="fuel-notes"
                    />
                </Field>
            </div>
            <ModalFooter onClose={onClose} onSave={save} saving={saving} saveLabel="Save fuel entry" testid="save-fuel-btn" />
        </ModalShell>
    );
}

/* ---------- Per-machine breakdown modal ---------- */
function MachineBreakdownModal({ initialMachineId, onClose, onSaved }) {
    const [machines, setMachines] = useState([]);
    const [form, setForm] = useState({
        machine_id: initialMachineId || "",
        entry_date: new Date().toISOString().slice(0, 10),
        reason: "",
        cost_zar: "",
        downtime_hours: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/fel/machines").then((r) => setMachines(r.data || []))
            .catch((e) => toast.error(formatApiError(e)));
    }, []);

    const save = async () => {
        if (!form.machine_id) return toast.error("Pick a machine");
        if (!form.reason) return toast.error("Reason is required");
        setSaving(true);
        try {
            await api.post("/fleet/machine-breakdowns", {
                machine_id: form.machine_id,
                entry_date: form.entry_date,
                reason: form.reason,
                cost_zar: Number(form.cost_zar || 0),
                downtime_hours: Number(form.downtime_hours || 0),
                notes: form.notes || null,
            });
            toast.success("Breakdown cost saved");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        setSaving(false);
    };

    return (
        <ModalShell title="Log breakdown cost (per machine)" onClose={onClose} testid="machine-breakdown-modal">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Machine">
                    <select
                        className="g-input"
                        value={form.machine_id}
                        onChange={(e) => setForm({ ...form, machine_id: e.target.value })}
                        data-testid="bk-machine-select"
                    >
                        <option value="">Select machine…</option>
                        {machines.map((m) => (
                            <option key={m.id} value={m.id}>
                                {m.machine_number} · {CATEGORY_LABEL[m.category] || ""}
                            </option>
                        ))}
                    </select>
                </Field>
                <Field label="Date">
                    <input
                        type="date"
                        className="g-input"
                        value={form.entry_date}
                        onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                        data-testid="bk-date"
                    />
                </Field>
                <Field label="Reason / failure mode" colspan={2}>
                    <input
                        type="text"
                        className="g-input"
                        value={form.reason}
                        onChange={(e) => setForm({ ...form, reason: e.target.value })}
                        placeholder="e.g. Hydraulic hose burst"
                        maxLength={300}
                        data-testid="bk-reason"
                    />
                </Field>
                <Field label="Cost (R)">
                    <input
                        type="number"
                        inputMode="decimal"
                        step="any"
                        min="0"
                        className="g-input tabular"
                        value={form.cost_zar}
                        onChange={(e) => setForm({ ...form, cost_zar: e.target.value })}
                        data-testid="bk-cost"
                    />
                </Field>
                <Field label="Downtime (hours)">
                    <input
                        type="number"
                        inputMode="decimal"
                        step="any"
                        min="0"
                        className="g-input tabular"
                        value={form.downtime_hours}
                        onChange={(e) => setForm({ ...form, downtime_hours: e.target.value })}
                        data-testid="bk-downtime"
                    />
                </Field>
                <Field label="Notes (optional)" colspan={2}>
                    <textarea
                        rows={2}
                        className="g-input"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        maxLength={500}
                        data-testid="bk-notes"
                    />
                </Field>
            </div>
            <ModalFooter onClose={onClose} onSave={save} saving={saving} saveLabel="Save breakdown" testid="save-bk-btn" />
        </ModalShell>
    );
}

/* ---------- Per-machine detail modal ---------- */
function MachineDetailModal({ machineRow, period, canLog, onClose, onChanged }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sub, setSub] = useState(null); // 'fuel' | 'bk' | 'trip' | {edit_trip: trip}
    const [tab, setTab] = useState("overview"); // 'overview' | 'fuel' | 'bk' | 'trips'

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/fleet/machines-monthly/${machineRow.machine_id}`, {
                params: { period_label: period },
            });
            setDetail(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [machineRow.machine_id, period]);

    const removeFuel = async (id) => {
        if (!window.confirm("Delete this fuel entry?")) return;
        try {
            await api.delete(`/fleet/fuel/${id}`);
            toast.success("Fuel entry removed");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const removeBk = async (id) => {
        if (!window.confirm("Delete this breakdown record?")) return;
        try {
            await api.delete(`/fleet/machine-breakdowns/${id}`);
            toast.success("Breakdown removed");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const removeTrip = async (id) => {
        if (!window.confirm("Delete this trip log entry?")) return;
        try {
            await api.delete(`/fleet/trips/${id}`);
            toast.success("Trip removed");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const releaseToService = async () => {
        if (!window.confirm(`Release ${detail?.machine?.machine_number} back to service?`)) return;
        try {
            await api.post(`/fel/machines/${detail.machine.id}/in-service`);
            toast.success(`${detail.machine.machine_number} is back in service`);
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading || !detail) {
        return (
            <ModalShell title="Machine details" onClose={onClose} testid="machine-detail-modal">
                <div className="p-8 text-center text-slate-400 text-sm">Loading machine details…</div>
            </ModalShell>
        );
    }

    const m = detail.machine;
    const fm = detail.fuel_month;
    const bm = detail.breakdowns_month;
    const tm = detail.trips_month || { items: [], count: 0, total_minutes: 0, total_distance_km: 0, total_hours_delta: 0 };
    const ts = detail.time_summary || { sessions_count: 0, operated_hours: 0, standing_hours: 0, breakdown_hours: 0, total_hours: 0,
                                        operated_minutes: 0, standing_minutes: 0, breakdown_minutes: 0, total_minutes: 0 };
    const ytd = detail.ytd;
    const isPassenger = m.category === "passenger_vehicle";

    const TABS = [
        { k: "overview", label: "Overview", Icon: Info },
        { k: "fuel",     label: `Fuel (${fm.count})`,    Icon: GasPump },
        { k: "bk",       label: `Breakdowns (${bm.count})`, Icon: Wrench },
        { k: "trips",    label: `Trip log (${tm.count})`,   Icon: Path },
    ];

    return (
        <ModalShell title={`${m.machine_number} · ${period}`} onClose={onClose} testid="machine-detail-modal" wide>
            {/* Out-of-Service banner with Release-to-service action (fleet manager / admin only) */}
            {!m.in_service && (
                <div className="mb-3 rounded-sm border-2 border-[var(--grindrod-red)] bg-red-50 p-3 flex items-start gap-3"
                     data-testid="out-of-service-banner">
                    <div className="flex-1">
                        <div className="font-display font-black uppercase tracking-wide text-[var(--grindrod-red)] text-[12px] mb-0.5">
                            ⛔ Machine is OUT OF SERVICE
                        </div>
                        <div className="text-[12px] text-slate-700 mb-0.5">
                            {m.out_of_service_reason || "No reason recorded"}
                        </div>
                        <div className="text-[10.5px] text-slate-500">
                            {m.out_of_service_source === "operator_pause" ? "Auto-flagged by operator pause · " : ""}
                            Flagged by {m.out_of_service_by || "—"}
                            {m.out_of_service_at ? ` · ${new Date(m.out_of_service_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                        </div>
                    </div>
                    {canLog && (
                        <button onClick={releaseToService} className="g-btn-primary text-xs whitespace-nowrap"
                                data-testid="release-to-service-btn">
                            <CheckCircle size={12} weight="fill" /> Release back to service
                        </button>
                    )}
                </div>
            )}

            {/* Tab strip */}
            <div className="flex gap-1 mb-3 border-b border-slate-200 overflow-x-auto" data-testid="machine-detail-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.k}
                        onClick={() => setTab(t.k)}
                        data-testid={`machine-detail-tab-${t.k}`}
                        className={`px-3 py-2 text-[11px] font-display font-bold uppercase tracking-wide border-b-2 transition flex items-center gap-1.5 ${
                            tab === t.k
                                ? "text-[var(--grindrod-navy)] border-[var(--grindrod-navy)]"
                                : "text-slate-500 border-transparent hover:text-slate-800"
                        }`}
                    >
                        <t.Icon size={12} weight="fill" /> {t.label}
                    </button>
                ))}
            </div>

            {tab === "overview" && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                        <Info2 label="Category" value={CATEGORY_LABEL[m.category] || "—"} />
                        <Info2 label="Owner division" value={m.owner_division || "—"} />
                        <Info2 label="Type" value={m.is_hired ? `Hired${m.hire_company ? " · " + m.hire_company : ""}` : "Owned"} />
                        <Info2 label="Status" value={m.in_service ? "In service" : "Out of service"} accent={m.in_service ? "green" : "red"} />
                        <Info2 label="Make / model" value={`${m.make || "—"} ${m.model || ""}`} />
                        <Info2 label={isPassenger ? "Current odometer (km)" : "Current hours"} value={(m.current_hours || 0).toFixed(1)} />
                        <Info2 label="Next service @" value={(m.next_service_hours || 0).toFixed(1)} />
                        <Info2 label={isPassenger ? "Km until service" : "Hours until service"} value={(m.hours_until_service || 0).toFixed(1)}
                               accent={m.hours_until_service <= 0 ? "red" : m.hours_until_service <= 36 ? "amber" : "green"} />
                    </div>

                    {/* Time-breakdown — operated · standing · breakdown */}
                    <TimeBreakdownStrip ts={ts} />

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                        <Pill label="Fuel (month)" value={fmtL(fm.litres)} accent="green" />
                        <Pill label="Fuel cost (month)" value={fmtZAR(fm.cost_zar)} />
                        <Pill label="Breakdown cost (month)" value={fmtZAR(bm.cost_zar)} accent="red" />
                        <Pill label="Downtime (month)" value={`${bm.downtime_hours.toFixed(1)} h`} />
                        <Pill label={isPassenger ? "Distance (month)" : "Hours operated (month)"}
                              value={isPassenger ? fmtKm(tm.total_distance_km) : `${(ts.operated_hours || 0).toFixed(1)} h`}
                              accent="green" />
                        <Pill label="Trips (month)" value={tm.count} />
                        <Pill label="Fuel YTD" value={fmtL(ytd.fuel_litres)} accent="green" />
                        <Pill label="Total spend YTD" value={fmtZAR(ytd.total_cost_zar)} />
                    </div>
                </>
            )}

            {/* Fleet manager action bar — context-aware to active tab */}
            {canLog && tab !== "overview" && (
                <div className="flex gap-2 mb-3">
                    {tab === "fuel" && (
                        <button onClick={() => setSub("fuel")} className="g-btn-primary text-xs" data-testid="detail-add-fuel">
                            <Plus size={12} weight="bold" /> Add fuel
                        </button>
                    )}
                    {tab === "bk" && (
                        <button onClick={() => setSub("bk")} className="g-btn-outline text-xs" data-testid="detail-add-bk">
                            <Wrench size={12} weight="bold" /> Add breakdown
                        </button>
                    )}
                    {tab === "trips" && (
                        <button onClick={() => setSub("trip")} className="g-btn-primary text-xs" data-testid="detail-add-trip">
                            <Plus size={12} weight="bold" /> Add manual trip
                        </button>
                    )}
                </div>
            )}

            {tab === "fuel" && (
                <Section title={`Fuel entries (${fm.count})`} Icon={GasPump}>
                    {fm.items.length === 0 ? (
                        <Empty msg="No fuel entries for this month." />
                    ) : (
                        <table className="w-full text-xs tabular">
                            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                                <tr>
                                    <th className="text-left p-2">Date</th>
                                    <th className="text-right p-2">Litres</th>
                                    <th className="text-right p-2">Cost</th>
                                    <th className="text-right p-2">Hour reading</th>
                                    <th className="text-left p-2">Notes</th>
                                    <th className="text-left p-2">By</th>
                                    {canLog && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {fm.items.map((f) => (
                                    <tr key={f.id} className="border-t border-slate-100">
                                        <td className="p-2">{f.entry_date}</td>
                                        <td className="p-2 text-right font-semibold">{fmtL(f.litres)}</td>
                                        <td className="p-2 text-right">{fmtZAR(f.cost_zar)}</td>
                                        <td className="p-2 text-right">{f.hours_reading ?? "—"}</td>
                                        <td className="p-2 text-slate-500">{f.notes || "—"}</td>
                                        <td className="p-2 text-slate-500">{f.logged_by_name}</td>
                                        {canLog && (
                                            <td className="p-2 text-right">
                                                <button onClick={() => removeFuel(f.id)} className="text-[var(--grindrod-red)] hover:opacity-80" title="Delete"><Trash size={12} /></button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Section>
            )}

            {tab === "bk" && (
                <Section title={`Breakdown records (${bm.count})`} Icon={Wrench}>
                    {bm.items.length === 0 ? (
                        <Empty msg="No breakdown records for this month." />
                    ) : (
                        <table className="w-full text-xs tabular">
                            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                                <tr>
                                    <th className="text-left p-2">Date</th>
                                    <th className="text-left p-2">Reason</th>
                                    <th className="text-right p-2">Cost</th>
                                    <th className="text-right p-2">Downtime</th>
                                    <th className="text-left p-2">Notes</th>
                                    <th className="text-left p-2">By</th>
                                    {canLog && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {bm.items.map((b) => (
                                    <tr key={b.id} className="border-t border-slate-100">
                                        <td className="p-2">{b.entry_date}</td>
                                        <td className="p-2 font-medium text-[var(--grindrod-navy)]">
                                            {b.reason}
                                            {b.source === "auto" && (
                                                <span className="ml-2 g-pill g-pill-green normal-case tracking-normal" data-testid={`bk-auto-${b.id}`}>Auto</span>
                                            )}
                                        </td>
                                        <td className="p-2 text-right text-[var(--grindrod-red)] font-semibold">{fmtZAR(b.cost_zar)}</td>
                                        <td className="p-2 text-right">{b.downtime_hours.toFixed(1)} h</td>
                                        <td className="p-2 text-slate-500">{b.notes || "—"}</td>
                                        <td className="p-2 text-slate-500">{b.logged_by_name}</td>
                                        {canLog && (
                                            <td className="p-2 text-right">
                                                <button onClick={() => removeBk(b.id)} className="text-[var(--grindrod-red)] hover:opacity-80" title="Delete"><Trash size={12} /></button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Section>
            )}

            {tab === "trips" && (
                <Section title={`Trip log (${tm.count})`} Icon={Path}>
                    <div className="px-2 pt-2">
                        <TimeBreakdownStrip ts={ts} compact />
                    </div>
                    {tm.items.length === 0 ? (
                        <Empty msg="No trips logged for this month. Trips are auto-saved when a FEL operator stops a session — or add a manual trip above." />
                    ) : (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 px-2">
                                <Pill label="Trips" value={tm.count} />
                                <Pill label="Operating time" value={fmtMin(tm.total_minutes)} accent="green" />
                                <Pill label={isPassenger ? "Distance" : "Hours delta"}
                                      value={isPassenger ? fmtKm(tm.total_distance_km) : `${(tm.total_hours_delta || 0).toFixed(1)} h`}
                                      accent="green" />
                                <Pill label="Distance total" value={fmtKm(tm.total_distance_km)} />
                            </div>
                            <table className="w-full text-xs tabular" data-testid="trips-table">
                                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                                    <tr>
                                        <th className="text-left p-2">Date</th>
                                        <th className="text-left p-2">Operator / driver</th>
                                        <th className="text-left p-2">Time frame</th>
                                        <th className="text-right p-2">Duration</th>
                                        {isPassenger ? (
                                            <>
                                                <th className="text-right p-2">Odo start → end</th>
                                                <th className="text-right p-2">Distance</th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="text-right p-2">Hrs start → end</th>
                                                <th className="text-right p-2">Hrs ∆</th>
                                                <th className="text-right p-2">Distance</th>
                                            </>
                                        )}
                                        <th className="text-left p-2">Used for</th>
                                        <th className="text-left p-2">Source</th>
                                        {canLog && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tm.items.map((t) => {
                                        const fmtT = (iso) => {
                                            if (!iso) return "—";
                                            const d = new Date(iso);
                                            return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
                                        };
                                        return (
                                            <tr key={t.id} className="border-t border-slate-100" data-testid={`trip-row-${t.id}`}>
                                                <td className="p-2">{t.trip_date}</td>
                                                <td className="p-2 text-[var(--grindrod-navy)] font-medium">{t.operator_name || "—"}</td>
                                                <td className="p-2 tabular text-[11px]">{fmtT(t.start_time)} → {fmtT(t.end_time)}</td>
                                                <td className="p-2 text-right">{fmtMin(t.duration_minutes)}</td>
                                                {isPassenger ? (
                                                    <>
                                                        <td className="p-2 text-right">{t.odometer_start ?? "—"} → {t.odometer_end ?? "—"}</td>
                                                        <td className="p-2 text-right font-semibold">{t.distance_km != null ? fmtKm(t.distance_km) : "—"}</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="p-2 text-right">{t.hours_start ?? "—"} → {t.hours_end ?? "—"}</td>
                                                        <td className="p-2 text-right font-semibold">{t.hours_delta != null ? `${t.hours_delta.toFixed(1)} h` : "—"}</td>
                                                        <td className="p-2 text-right">{t.distance_km != null ? fmtKm(t.distance_km) : "—"}</td>
                                                    </>
                                                )}
                                                <td className="p-2 text-slate-600 truncate max-w-[160px]" title={t.used_for}>{t.used_for || "—"}</td>
                                                <td className="p-2">
                                                    <span className={`g-pill normal-case tracking-normal ${t.source === "auto" ? "g-pill-green" : "g-pill-navy"}`}>
                                                        {t.source === "auto" ? "Auto" : "Manual"}
                                                    </span>
                                                </td>
                                                {canLog && (
                                                    <td className="p-2 text-right whitespace-nowrap">
                                                        <button onClick={() => setSub({ edit_trip: t })} className="text-slate-500 hover:text-[var(--grindrod-navy)] mr-2" title="Edit / add notes" data-testid={`edit-trip-${t.id}`}>
                                                            <PencilSimple size={12} />
                                                        </button>
                                                        <button onClick={() => removeTrip(t.id)} className="text-[var(--grindrod-red)] hover:opacity-80" title="Delete" data-testid={`delete-trip-${t.id}`}>
                                                            <Trash size={12} />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}
                </Section>
            )}

            {sub === "fuel" && (
                <FuelEntryModal
                    initialMachineId={m.id}
                    onClose={() => setSub(null)}
                    onSaved={() => { setSub(null); load(); onChanged?.(); }}
                />
            )}
            {sub === "bk" && (
                <MachineBreakdownModal
                    initialMachineId={m.id}
                    onClose={() => setSub(null)}
                    onSaved={() => { setSub(null); load(); onChanged?.(); }}
                />
            )}
            {sub === "trip" && (
                <TripLogModal
                    initialMachine={m}
                    onClose={() => setSub(null)}
                    onSaved={() => { setSub(null); load(); onChanged?.(); }}
                />
            )}
            {sub && typeof sub === "object" && sub.edit_trip && (
                <TripLogModal
                    initialMachine={m}
                    editTrip={sub.edit_trip}
                    onClose={() => setSub(null)}
                    onSaved={() => { setSub(null); load(); onChanged?.(); }}
                />
            )}
        </ModalShell>
    );
}

/* ---------- Trip log entry / edit modal ---------- */
function TripLogModal({ initialMachine, editTrip, onClose, onSaved }) {
    const isPassenger = initialMachine?.category === "passenger_vehicle";
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState(() => {
        if (editTrip) {
            return {
                operator_name: editTrip.operator_name || "",
                trip_date: editTrip.trip_date || today,
                start_hm: editTrip.start_time ? new Date(editTrip.start_time).toTimeString().slice(0, 5) : "",
                end_hm: editTrip.end_time ? new Date(editTrip.end_time).toTimeString().slice(0, 5) : "",
                hours_start: editTrip.hours_start ?? "",
                hours_end: editTrip.hours_end ?? "",
                odometer_start: editTrip.odometer_start ?? "",
                odometer_end: editTrip.odometer_end ?? "",
                distance_km: editTrip.distance_km ?? "",
                used_for: editTrip.used_for || "",
                notes: editTrip.notes || "",
            };
        }
        return {
            operator_name: "",
            trip_date: today,
            start_hm: "",
            end_hm: "",
            hours_start: "",
            hours_end: "",
            odometer_start: "",
            odometer_end: "",
            distance_km: "",
            used_for: "",
            notes: "",
        };
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!form.trip_date) return toast.error("Trip date is required");
        if (!editTrip && !form.used_for) return toast.error("What was the machine used for?");
        const startISO = form.start_hm ? `${form.trip_date}T${form.start_hm}:00` : null;
        const endISO = form.end_hm ? `${form.trip_date}T${form.end_hm}:00` : null;
        let dur = null;
        if (startISO && endISO) {
            const d = (new Date(endISO).getTime() - new Date(startISO).getTime()) / 60000;
            if (d > 0) dur = d;
        }
        setSaving(true);
        try {
            const payload = {
                operator_name: form.operator_name || null,
                used_for: form.used_for || null,
                notes: form.notes || null,
                distance_km: form.distance_km !== "" ? Number(form.distance_km) : null,
            };
            if (isPassenger) {
                payload.odometer_start = form.odometer_start !== "" ? Number(form.odometer_start) : null;
                payload.odometer_end = form.odometer_end !== "" ? Number(form.odometer_end) : null;
            } else {
                payload.hours_start = form.hours_start !== "" ? Number(form.hours_start) : null;
                payload.hours_end = form.hours_end !== "" ? Number(form.hours_end) : null;
            }
            if (editTrip) {
                await api.patch(`/fleet/trips/${editTrip.id}`, payload);
                toast.success("Trip updated");
            } else {
                await api.post("/fleet/trips", {
                    machine_id: initialMachine.id,
                    trip_date: form.trip_date,
                    start_time: startISO,
                    end_time: endISO,
                    duration_minutes: dur,
                    ...payload,
                });
                toast.success("Trip logged");
            }
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        setSaving(false);
    };

    return (
        <ModalShell title={`${editTrip ? "Edit trip" : "Log manual trip"} · ${initialMachine.machine_number}`} onClose={onClose} testid="trip-log-modal">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Operator / driver">
                    <input type="text" className="g-input" value={form.operator_name}
                           onChange={(e) => setForm({ ...form, operator_name: e.target.value })}
                           data-testid="trip-operator" />
                </Field>
                <Field label="Date">
                    <input type="date" className="g-input" value={form.trip_date}
                           onChange={(e) => setForm({ ...form, trip_date: e.target.value })}
                           data-testid="trip-date" />
                </Field>
                <Field label="Start time">
                    <input type="time" className="g-input" value={form.start_hm}
                           onChange={(e) => setForm({ ...form, start_hm: e.target.value })}
                           data-testid="trip-start-time" />
                </Field>
                <Field label="End time">
                    <input type="time" className="g-input" value={form.end_hm}
                           onChange={(e) => setForm({ ...form, end_hm: e.target.value })}
                           data-testid="trip-end-time" />
                </Field>

                {isPassenger ? (
                    <>
                        <Field label="Odometer start (km)">
                            <input type="number" inputMode="decimal" step="any" min="0" className="g-input tabular"
                                   value={form.odometer_start}
                                   onChange={(e) => setForm({ ...form, odometer_start: e.target.value })}
                                   data-testid="trip-odo-start" />
                        </Field>
                        <Field label="Odometer end (km)">
                            <input type="number" inputMode="decimal" step="any" min="0" className="g-input tabular"
                                   value={form.odometer_end}
                                   onChange={(e) => setForm({ ...form, odometer_end: e.target.value })}
                                   data-testid="trip-odo-end" />
                        </Field>
                    </>
                ) : (
                    <>
                        <Field label="Machine hours start">
                            <input type="number" inputMode="decimal" step="any" min="0" className="g-input tabular"
                                   value={form.hours_start}
                                   onChange={(e) => setForm({ ...form, hours_start: e.target.value })}
                                   data-testid="trip-hours-start" />
                        </Field>
                        <Field label="Machine hours end">
                            <input type="number" inputMode="decimal" step="any" min="0" className="g-input tabular"
                                   value={form.hours_end}
                                   onChange={(e) => setForm({ ...form, hours_end: e.target.value })}
                                   data-testid="trip-hours-end" />
                        </Field>
                    </>
                )}

                <Field label="Distance (km, optional if auto-calc from odo)">
                    <input type="number" inputMode="decimal" step="any" min="0" className="g-input tabular"
                           value={form.distance_km}
                           onChange={(e) => setForm({ ...form, distance_km: e.target.value })}
                           data-testid="trip-distance" />
                </Field>
                <Field label="Used for">
                    <input type="text" className="g-input" value={form.used_for}
                           onChange={(e) => setForm({ ...form, used_for: e.target.value })}
                           placeholder="e.g. Loading MV Exaro — Manganese hatch 2"
                           maxLength={200}
                           data-testid="trip-used-for" />
                </Field>
                <Field label="Notes (optional)" colspan={2}>
                    <textarea rows={2} className="g-input" value={form.notes}
                              onChange={(e) => setForm({ ...form, notes: e.target.value })}
                              maxLength={500}
                              data-testid="trip-notes" />
                </Field>
            </div>
            <ModalFooter onClose={onClose} onSave={save} saving={saving} saveLabel={editTrip ? "Save changes" : "Log trip"} testid="save-trip-btn" />
        </ModalShell>
    );
}

/* ---------- Small helpers ---------- */
function ModalShell({ title, onClose, children, testid, wide }) {
    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-6" onClick={onClose}>
            <div
                className={`bg-white rounded-lg shadow-2xl w-full ${wide ? "max-w-4xl" : "max-w-xl"} max-h-[92vh] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}
                data-testid={testid}
            >
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 sticky top-0 bg-white z-10">
                    <div className="font-display font-bold uppercase tracking-wide text-sm flex-1">{title}</div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="modal-close-btn">
                        <X size={16} weight="bold" />
                    </button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}
function ModalFooter({ onClose, onSave, saving, saveLabel, testid }) {
    return (
        <div className="mt-5 flex justify-end gap-2 pt-3 border-t border-slate-200">
            <button onClick={onClose} className="g-btn-ghost-sm text-xs">Cancel</button>
            <button onClick={onSave} disabled={saving} className="g-btn-primary text-xs" data-testid={testid}>
                {saving ? "Saving…" : saveLabel}
            </button>
        </div>
    );
}
function Field({ label, children, colspan }) {
    return (
        <div className={colspan === 2 ? "sm:col-span-2" : ""}>
            <div className="uppercase-label text-[9.5px] mb-1">{label}</div>
            {children}
        </div>
    );
}
function Info2({ label, value, accent }) {
    const color = accent === "red" ? "var(--grindrod-red)"
        : accent === "green" ? "var(--grindrod-green)"
        : accent === "amber" ? "#B36B00"
        : "var(--grindrod-navy)";
    return (
        <div className="g-card p-2.5">
            <div className="uppercase-label text-[9.5px] mb-0.5">{label}</div>
            <div className="font-display font-bold text-sm tabular truncate" style={{ color }}>{value}</div>
        </div>
    );
}
function Pill({ label, value, accent }) {
    const color = accent === "red" ? "var(--grindrod-red)" : accent === "green" ? "var(--grindrod-green)" : "var(--grindrod-navy)";
    return (
        <div className="g-card p-2.5">
            <div className="uppercase-label text-[9.5px]">{label}</div>
            <div className="font-display font-black text-lg tabular" style={{ color }}>{value}</div>
        </div>
    );
}
function Section({ title, Icon, children }) {
    return (
        <div className="g-card overflow-hidden mt-3">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-2">
                <Icon size={12} weight="fill" /> {title}
            </div>
            <div className="overflow-x-auto">{children}</div>
        </div>
    );
}
function Empty({ msg }) {
    return <div className="p-4 text-center text-xs text-slate-400">{msg}</div>;
}

/* ---------- Time-breakdown strip (operated / standing / breakdown) ---------- */
function TimeBreakdownStrip({ ts, compact }) {
    const total = Math.max(0.001, ts.total_minutes || 0);
    const op = ts.operated_minutes || 0;
    const sd = ts.standing_minutes || 0;
    const bd = ts.breakdown_minutes || 0;
    const pct = (v) => Math.max(0, Math.min(100, (v / total) * 100));
    const opPct = pct(op), sdPct = pct(sd), bdPct = pct(bd);
    return (
        <div className={`g-card ${compact ? "p-2.5" : "p-3"}`} data-testid="time-breakdown-strip">
            <div className="flex items-center justify-between mb-2">
                <div className="uppercase-label text-[9.5px] flex items-center gap-1.5">
                    <Clock size={11} weight="fill" /> Time breakdown · {ts.sessions_count || 0} session{(ts.sessions_count || 0) === 1 ? "" : "s"}
                </div>
                <div className="text-[10.5px] text-slate-500 tabular">Total {fmtMin(total)}</div>
            </div>
            {/* Stacked bar */}
            <div className="flex w-full h-3 rounded-sm overflow-hidden bg-slate-100" data-testid="time-bar">
                {opPct > 0 && <div title={`Operated ${fmtMin(op)}`} style={{ width: `${opPct}%`, background: "var(--grindrod-green)" }} data-testid="bar-operated" />}
                {sdPct > 0 && <div title={`Standing ${fmtMin(sd)}`} style={{ width: `${sdPct}%`, background: "#FFB300" }} data-testid="bar-standing" />}
                {bdPct > 0 && <div title={`Breakdown ${fmtMin(bd)}`} style={{ width: `${bdPct}%`, background: "var(--grindrod-red)" }} data-testid="bar-breakdown" />}
            </div>
            {/* Legend pills */}
            <div className="grid grid-cols-3 gap-2 mt-2">
                <LegendPill label="Operated" value={`${(ts.operated_hours || 0).toFixed(1)} h`} percent={opPct} colour="var(--grindrod-green)" testid="legend-operated" />
                <LegendPill label="Standing" value={`${(ts.standing_hours || 0).toFixed(1)} h`} percent={sdPct} colour="#FFB300" testid="legend-standing" />
                <LegendPill label="Breakdown" value={`${(ts.breakdown_hours || 0).toFixed(1)} h`} percent={bdPct} colour="var(--grindrod-red)" testid="legend-breakdown" />
            </div>
        </div>
    );
}
function LegendPill({ label, value, percent, colour, testid }) {
    return (
        <div className="flex items-center gap-2" data-testid={testid}>
            <span className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: colour }} />
            <div className="min-w-0">
                <div className="uppercase-label text-[9px] truncate">{label}</div>
                <div className="font-display font-bold text-[12px] tabular" style={{ color: colour }}>
                    {value} <span className="text-slate-400 font-normal">· {percent.toFixed(0)}%</span>
                </div>
            </div>
        </div>
    );
}
